//dependencies
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { nanoid } from "nanoid/non-secure";
import React, { useEffect, useRef, useState } from "react";
import { LoaderFunctionArgs, useParams } from "react-router-dom";

import { faFilePdf, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { faClone, faEye, faFloppyDisk, faTrashCan } from "@fortawesome/pro-solid-svg-icons";

//py-components
import { ButtonStandard, FormHandle, toast } from "@performyard/py-components";

//components
import Breadcrumbs from "../../../../components/Breadcrumbs";
import Card from "../../../../components/Card";
import Spacer from "../../../../components/Spacer";
import DeleteFormModal from "../Forms/components/DeleteFormModal";
import TitleInput from "./components/EditFormTitleInput";
import EditQuestionBlock from "./components/EditQuestionBlock";
import QuestionBlock from "./components/QuestionBlock";
import SaveFormAsCopyModal from "./components/SaveFormAsCopyModal";

//hooks
import {
	useCompanyForm,
	useCreateCompanyForm,
	useGetPreviewForm,
	useGetPreviewFormPDF,
	useMutateCompanyForm,
} from "../../../../api/hooks";
import useUnsavedChanges from "../../../../hooks/useUnsavedChanges";
import { useEditFormStore } from "./useEditFormStore";

//queries
import {
	companySettingsQuery,
	currentUserQuery,
	rbacPermissionsQuery,
	useCompanyFormQuery,
} from "../../../../api/queries";
import { SimplifiedQuery, dataLoader } from "../../../../api/queries/dataLoader";

import { markdownToHtml, markdownify } from "../../../../utils/markdown";
import { ReviewQuestion, ReviewQuestionSchema, ReviewQuestionsSchema, normalizeQuestions } from "./utils";

//template
import BasePageTemplate from "../../../../pageTemplates/BasePageTemplate";

declare global {
	interface Window {
		markdownToHtml: (markdown: string) => string;
		markdownify: (elements: HTMLCollectionOf<Element>, addClassName?: boolean) => void;
	}
}

// make markdownify available to the global window object
window.markdownify = markdownify;
window.markdownToHtml = markdownToHtml;

const NAME_TRUNCATION_LENGTH = 40;

export const adminReviewFormLoader = async ({ params }: LoaderFunctionArgs) => {
	const baseFormId = params.baseFormId || "";
	const queries: Array<() => SimplifiedQuery> = [];

	queries.push(() => rbacPermissionsQuery("review"));
	queries.push(() => useCompanyFormQuery(baseFormId));
	queries.push(() => companySettingsQuery());

	return await dataLoader([[currentUserQuery], queries]);
};

const EditForm = () => {
	const activeQuestionFormRef = useRef<FormHandle<typeof ReviewQuestionSchema>>(null);

	const { baseFormId = "" } = useParams();
	const { data, isLoading } = useCompanyForm(baseFormId);
	const formID = data?._id || "";
	const trackRef = useRef<HTMLDivElement | null>(null);
	const [isDeleteFormModalOpen, setIsDeleteFormModalOpen] = useState(false);
	const [isSetIsSaveFormAsCopyModalOpen, setIsSaveFormAsCopyModalOpen] = useState(false);
	const isInitializedRef = useRef(false);

	const displaySuccessToast = () => {
		toast.success("Form has been saved");
	};

	const { mutate } = useMutateCompanyForm({
		onSuccess: (_, variables) => {
			if (variables?.baseId === baseFormId) {
				setHasChanges(false);
				displaySuccessToast();
			}
		},
	});
	const { mutate: getPreviewPDF } = useGetPreviewFormPDF();
	const { mutate: getPreview } = useGetPreviewForm({
		onSuccess: (response) => {
			const responseUrl = response?.previewUrl || "";

			if (responseUrl) {
				window.open(responseUrl, "_blank");
			}
		},
	});

	const { mutate: createFormCopy } = useCreateCompanyForm({
		onSuccess: (response) => {
			const { _id } = response;

			setIsSaveFormAsCopyModalOpen(false);

			// open form copy in new tab
			const win = window.open(`/edit-review-form/${_id}`, "_blank");
			if (win) {
				win.focus();
			}
		},
	});
	const { setIsUnsafe, setIsSafe } = useUnsavedChanges();

	const {
		editId,
		questions,
		setQuestions,
		setName,
		name,
		validateName,
		hasChanges,
		setHasChanges,
		setEditingQuestion,
		reset,
		reorderQuestions,
		addQuestion,
	} = useEditFormStore((state) => ({
		editId: state?.editingQuestion?.refId,
		questions: state.questions,
		setQuestions: state.setQuestions,
		setName: state.setName,
		name: state.name,
		validateName: state.validateName,
		hasChanges: state.hasChanges,
		setHasChanges: state.setHasChanges,
		setEditingQuestion: state.setEditingQuestion,
		reset: state.reset,
		reorderQuestions: state.reorderQuestions,
		addQuestion: state.addQuestion,
	}));

	const updateForm = (formID, formName, formQuestions) => {
		mutate({
			baseId: formID,
			updates: {
				name: formName,
				questions: formQuestions.map((q) => ({
					...q,
					_id: undefined,
				})),
			},
		});
	};

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const validateQuestionsAndScrollToInvalid = (questionsToValidate: ReviewQuestion[]) => {
		const result = ReviewQuestionsSchema.safeParse({ questions: questionsToValidate });
		if (!result.success) {
			toast.error("Please fix all highlighted form errors before continuing");
			// Find the first invalid question and scroll to it
			const errorPath = result.error.errors[0]?.path;
			// path is ["questions", index, ...]
			if (errorPath && errorPath[0] === "questions" && typeof errorPath[1] === "number") {
				const errorIndex = errorPath[1];
				const errorQuestion = questionsToValidate[errorIndex];

				// Try to find the question element - check if it's in edit mode or display mode
				let questionElement = document.getElementById(`question-${errorQuestion?.refId}`); // EditQuestionBlock
				if (!questionElement) {
					questionElement = document.getElementById(`question-block-${errorIndex}`); // QuestionBlock
				}

				if (questionElement) {
					questionElement.scrollIntoView({ behavior: "smooth", block: "center" });
				}
			}
			return false;
		}
		return true;
	};

	// initializing the form on load
	useEffect(() => {
		if (!isLoading && data && !isInitializedRef.current) {
			setName(data?.name || "");
			const rawQuestions = data?.questions || [];
			const convertedQuestions = rawQuestions.map((q) => ({ ...q, refId: nanoid() }));

			// find all questions with a question array set, and remap their id to a refId
			const _idToNewRefIdMap = convertedQuestions.reduce((acc: { [key: string]: string }, question) => {
				acc[question._id] = question.refId;
				return acc;
			}, {});

			const updatedQuestions = convertedQuestions.map((question) => {
				const updatedQuestions = question?.questions?.map((subQuestion) => {
					const updatedSubQuestion = {
						...subQuestion,
						refId: _idToNewRefIdMap[subQuestion.id],
					};

					return updatedSubQuestion;
				});

				let aggregation = question.aggregation;
				if (question.type === "calculator" && !question.aggregation) {
					aggregation = "sum";
				}

				const updatedQuestion = {
					...question,
					questions: updatedQuestions,
					aggregation,
				};

				return updatedQuestion;
			});

			setQuestions(updatedQuestions);
			setHasChanges(false);
			isInitializedRef.current = true;
		}
	}, [data, isLoading]);

	// Validate questions on load and scroll to first invalid one
	useEffect(() => {
		if (!isLoading && isInitializedRef.current && questions.length > 0) {
			const normalized = normalizeQuestions(questions);
			validateQuestionsAndScrollToInvalid(normalized);
		}
	}, [isLoading]);

	// Cleanup function that only runs on component unmount
	useEffect(() => {
		return () => {
			reset();
			isInitializedRef.current = false;
		};
	}, []);

	// handling unsaved changes a little more manually
	useEffect(() => {
		if (hasChanges || editId) {
			setIsUnsafe();
		} else {
			setIsSafe();
		}
	}, [hasChanges, editId]);

	// Ensure hasChanges is true when editing a question
	useEffect(() => {
		if (editId) {
			setHasChanges(true);
		}
	}, [editId, setHasChanges]);

	const handleQuestionSubmit = (updatedQuestion: ReviewQuestion) => {
		// Update the question in the store
		const newQuestions = questions.map((q) => (q.refId === updatedQuestion.refId ? updatedQuestion : q));
		const normalized = normalizeQuestions(newQuestions);
		setQuestions(normalized);
		setHasChanges(true);
		setEditingQuestion(); // Clear editing state
	};

	const handleRequestEdit = async (refId: string) => {
		if (editId) {
			// If we are already editing a question, try to submit it
			if (activeQuestionFormRef.current) {
				const isValid = await activeQuestionFormRef.current.validate(true);
				if (isValid) {
					await activeQuestionFormRef.current.submit();
					// Success. Proceed to switch.
					setEditingQuestion(refId);
				}
			} else {
				// Should not happen if editId is set

				setEditingQuestion(refId);
			}
		} else {
			setEditingQuestion(refId);
		}
	};

	const handleSubmit = async (meta?: { action?: string }) => {
		// If editing, validate and submit the question first
		if (editId && activeQuestionFormRef.current) {
			const isValid = await activeQuestionFormRef.current.validate(true);
			if (!isValid) {
				// Validation failed, stop
				return;
			}
			await activeQuestionFormRef.current.submit();
			// Success, question updated in store. Proceed.
		}

		// Get the latest questions from the store (not from the hook selector which may be stale)
		const currentQuestions = useEditFormStore.getState().questions;
		const normalizedQuestions = normalizeQuestions(currentQuestions);

		// Check if there are no questions for actions that require questions
		const requiresQuestions = ["save-form", "save-form-as-copy", "preview", "preview-pdf"];
		if (requiresQuestions.includes(meta?.action || "") && normalizedQuestions.length === 0) {
			toast.error("Cannot perform this action. Please add at least one question to the form.");
			return;
		}

		switch (meta?.action) {
			case "save-form":
				if (validateName(name) && validateQuestionsAndScrollToInvalid(normalizedQuestions)) {
					updateForm(baseFormId, name, normalizedQuestions);
					// Don't set hasChanges to false here - let the onSuccess callback handle it
				}
				break;
			case "add-question": {
				const newRefId = nanoid();
				const newQuestion = normalizeQuestions([
					{
						refId: newRefId,
						type: "text" as const,
						question: "",
					},
				])[0];

				// Add to store
				addQuestion(newQuestion);

				// Switch to edit mode for this new question
				// Use request edit logic to ensure current edit is saved
				handleRequestEdit(newRefId);
				// Mark as new so Cancel deletes it
				setEditingQuestion(newRefId, true);
				break;
			}
			case "save-form-as-copy":
				if (validateQuestionsAndScrollToInvalid(normalizedQuestions)) {
					setIsSaveFormAsCopyModalOpen(true);
				}
				break;
			case "preview":
				if (validateQuestionsAndScrollToInvalid(normalizedQuestions)) {
					getPreview({ questions: normalizedQuestions, name });
				}
				break;
			case "preview-pdf":
				if (validateQuestionsAndScrollToInvalid(normalizedQuestions)) {
					getPreviewPDF({ questions: normalizedQuestions, name });
				}
				break;
			default:
				break;
		}
	};

	// handle older markdown content
	useEffect(() => {
		window.markdownify(document.getElementsByClassName("is-markdown"), false);
	}, [editId, questions.length]);

	// truncating name for breadcrumbs
	const truncatedName =
		(data?.name?.length || 0) > NAME_TRUNCATION_LENGTH
			? `${data?.name.slice(0, NAME_TRUNCATION_LENGTH)}...`
			: data?.name || "";

	return (
		<BasePageTemplate title="Edit Review" backgroundColor="grey">
			<div className="m-auto mt-[24px] w-[1104px] max-w-[calc(100vw-32px)]">
				<Breadcrumbs
					pages={[
						{
							pageID: "edit-review",
							label: "Review Forms",
							url: "/administration/reviews/forms/",
						},
						{
							pageID: "review-details",
							label: truncatedName,
							url: `/edit-review-form/${baseFormId}`,
						},
					]}
				/>
				<Spacer height={5} />

				<div className="flex gap-[16px]" ref={trackRef}>
					<Card className="flex-grow-1 flex-shrink-1 w-[800px]" padding="40px">
						<TitleInput defaultTitle={data?.name || ""} />
						<Spacer height={5} />
						<div className="w-full">
							<div className="w-full max-w-full">
								<DndContext
									sensors={sensors}
									collisionDetection={closestCenter}
									onDragEnd={(event) => {
										const { active, over } = event;
										reorderQuestions(active, over);
									}}
									modifiers={[restrictToVerticalAxis, restrictToParentElement]}
								>
									<SortableContext
										items={questions.map((q) => q.refId)}
										strategy={verticalListSortingStrategy}
									>
										<div key="questions-container">
											{questions.map((question, index) =>
												question.refId === editId ? (
													<EditQuestionBlock
														key={question.refId}
														question={question}
														index={index}
														formRef={activeQuestionFormRef as any}
														onSubmit={handleQuestionSubmit}
													/>
												) : (
													<QuestionBlock
														key={question.refId}
														refId={question.refId}
														index={index}
														onRequestEdit={handleRequestEdit}
													/>
												)
											)}
										</div>
										<div id="add-button" className="mt-6 w-full">
											<ButtonStandard
												color="primary"
												// We use regular onClick here since we manually handle the action
												onClick={() => handleSubmit({ action: "add-question" })}
												iconLeft={faPlus}
												size="medium"
												data-testid="add-question-button"
												dangerouslySetClassName="!w-full"
												type="button"
											>
												New Question
											</ButtonStandard>
										</div>
									</SortableContext>
								</DndContext>
							</div>
						</div>
					</Card>
					<div id="controls-track" className="max-w-[208px] flex-shrink-0">
						<Card className="sticky top-2 flex h-fit w-[176px] flex-col gap-2" padding="16px">
							<ButtonStandard
								iconLeft={faFloppyDisk}
								color="green"
								onClick={() => handleSubmit({ action: "save-form" })}
								size="medium"
								disabled={!hasChanges || questions.length === 0}
								type="button"
							>
								Save Form
							</ButtonStandard>
							<ButtonStandard
								onClick={() => handleSubmit({ action: "save-form-as-copy" })}
								iconLeft={faClone}
								variant="outline"
								color="neutral"
								type="button"
								disabled={questions.length === 0}
							>
								Save as Copy
							</ButtonStandard>
							<ButtonStandard
								onClick={() => handleSubmit({ action: "preview" })}
								variant="outline"
								iconLeft={faEye}
								color="neutral"
								type="button"
								disabled={questions.length === 0}
							>
								Preview
							</ButtonStandard>
							<ButtonStandard
								color="neutral"
								onClick={() => handleSubmit({ action: "preview-pdf" })}
								iconLeft={faFilePdf}
								variant="outline"
								type="button"
								disabled={questions.length === 0}
							>
								Preview as PDF
							</ButtonStandard>
							<ButtonStandard
								onClick={() => setIsDeleteFormModalOpen(true)}
								variant="outline"
								color="neutral"
								type="button"
								iconLeft={faTrashCan}
							>
								Delete Form
							</ButtonStandard>
						</Card>
					</div>
				</div>
			</div>
			<SaveFormAsCopyModal
				handleSaveFormAsCopy={(formCopyName) => {
					// Get the latest questions from the store
					const currentQuestions = useEditFormStore.getState().questions;
					const normalizedQuestions = normalizeQuestions(currentQuestions);

					// Validate questions (should already be validated from handleSubmit, but double-check)
					if (validateQuestionsAndScrollToInvalid(normalizedQuestions) && validateName(data?.name || "")) {
						createFormCopy({ name: formCopyName, questions: normalizedQuestions });
					} else {
						setIsSaveFormAsCopyModalOpen(false);
					}
				}}
				isModalOpen={isSetIsSaveFormAsCopyModalOpen}
				handleClose={() => setIsSaveFormAsCopyModalOpen(false)}
			/>
			<DeleteFormModal
				deleteFormId={isDeleteFormModalOpen ? formID : ""}
				handleClose={() => setIsDeleteFormModalOpen(false)}
				rerouteUrl={"/administration/reviews/forms/"}
				deleteFormName={data?.name || ""}
			/>
		</BasePageTemplate>
	);
};

const EditFormPage = () => {
	return <EditForm />;
};

export default EditFormPage;
